import React from "react";
import TravelAuthFormSection from "./TravelAuthFormSection";
import { BestSelect, DeleteIcon, FileAttachment } from "best-common-react";
import { inject, observer } from "mobx-react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";

const fileName = <FormattedMessage id="FileAttachments.fileName" defaultMessage="Filename" />,
  label = <FormattedMessage id="FileAttachments.label" defaultMessage="Label" />,
  lastModified = <FormattedMessage id="FileAttachments.lastModified" defaultMessage="Last Modified" />,
  size = <FormattedMessage id="FileAttachments.size" defaultMessage="Size" />;

const StyledSelect = styled(BestSelect)`
  height: 32px;
  div {
    height: 32px;
    min-height: 32px;
    line-height: 2rem;
  }
`;

const AttachmentLabelSelect = observer(({ row, travelAuthFormStore, lookupStore }) => {
  const currentFile =
    (travelAuthFormStore.travelAuthForm.fileAttachments || []).find(
      file => file.fileAttachmentId === row.fileAttachmentId
    ) || row;
  const options = travelAuthFormStore.attachmentLabelOptions;
  const selectedId =
    currentFile.attachmentTypes && currentFile.attachmentTypes.length > 0 ? currentFile.attachmentTypes[0] : null;
  const selected = selectedId !== null ? lookupStore.getDropdownItem(options, selectedId) : null;
  const fallbackLabel =
    currentFile.labels && currentFile.labels[0] ? currentFile.labels[0].label || currentFile.labels[0] : "";

  if (!travelAuthFormStore.isFieldEditable("fileAttachmentLabels")) {
    return <div>{(selected && selected.label) || fallbackLabel}</div>;
  }

  return (
    <StyledSelect
      value={selected || ""}
      options={options}
      onChange={option => travelAuthFormStore.updateFileLabel(row.fileAttachmentId, option)}
      portal={document.body}
    />
  );
});

AttachmentLabelSelect.propTypes = {
  row: PropTypes.object.isRequired,
  travelAuthFormStore: PropTypes.object.isRequired,
  lookupStore: PropTypes.object.isRequired
};

const FileAttachments = ({ rootStore: { travelAuthFormStore, lookupStore } }) => {
  const getFile = (event, fileName, fileIdentifier, contentType) => {
    event.preventDefault();
    travelAuthFormStore.getFile(fileName, fileIdentifier, contentType);
  };

  const FileNameFormatter = ({ row }) => {
    if (!!row.fileIdentifier) {
      //need to add this none existing #section to avoid node error
      return (
        <a href="#section" onClick={evt => getFile(evt, row.fileName, row.fileIdentifier, row.contentType)}>
          {row.fileName}
        </a>
      );
    } else {
      return <div>{row.fileName}</div>;
    }
  };

  const AttachmentLabelFormatter = data => {
    const row = data.row || data;

    return <AttachmentLabelSelect row={row} travelAuthFormStore={travelAuthFormStore} lookupStore={lookupStore} />;
  };

  const DeleteFormatter = data => {
    let row;
    let needsPadding = false;
    if (!!data.row) {
      // Desktop has .row, mobile does not
      row = data.row;
    } else {
      row = data;
      // Mobile needs padding
      needsPadding = true;
    }

    return (
      travelAuthFormStore.isFieldEditable("fileAttachmentsDelete") && (
        <DeleteIcon
          padding={needsPadding}
          deleteFunc={() => {
            travelAuthFormStore.deleteFileAttachment(row.fileAttachmentId);
          }}
        />
      )
    );
  };

  FileNameFormatter.propTypes = {
    row: PropTypes.object
  };
  AttachmentLabelFormatter.propTypes = {
    row: PropTypes.object,
    attachmentTypes: PropTypes.array,
    labels: PropTypes.array,
    fileAttachmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  };
  DeleteFormatter.propTypes = {
    row: PropTypes.object,
    fileAttachmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  };

  const cols = [
    {
      key: "fileName",
      name: fileName,
      formatter: FileNameFormatter
    },
    {
      key: "labels",
      name: label,
      width: 280,
      formatter: AttachmentLabelFormatter
    },
    {
      key: "timeStampAsString",
      name: lastModified,
      width: 200
    },
    {
      key: "fileSizeAsString",
      name: size,
      width: 100
    },
    {
      key: "delete",
      name: "",
      width: 40,
      formatter: DeleteFormatter
    }
  ];

  return (
    <div className="col-12">
      <TravelAuthFormSection
        title={<FormattedMessage id="FileAttachments.title" defaultMessage="File Attachment(s)" />}
      >
        <FileAttachment
          key={lookupStore.loaded ? "file-attachments-ready" : "file-attachments-loading"}
          cols={cols}
          editable={travelAuthFormStore.isFieldEditable("fileAttachments")}
          onFileAdd={files => travelAuthFormStore.addFileAttachments(files)}
          deleteFormatter={DeleteFormatter}
          files={travelAuthFormStore.travelAuthForm.fileAttachments}
          headerRowHeight={35}
        />
      </TravelAuthFormSection>
    </div>
  );
};

FileAttachments.propTypes = {
  rootStore: PropTypes.object.isRequired
};

export default inject("rootStore")(observer(FileAttachments));
